import { TimeSlot, CalendarDayData, PositionedAppointment } from '../calendarTypes';
import { WorkItem } from '../../workitems/workItemTypes';

/** Calendar day starts at 7:00 AM */
export const DAY_START_HOUR = 7;
/** Calendar day ends at 6:00 PM (18:00) */
export const DAY_END_HOUR = 18;
/** Time slot interval in minutes */
export const SLOT_INTERVAL = 30;

/**
 * Generate time slots for a calendar day (7:00 AM – 6:00 PM, 30-min intervals).
 */
export function generateTimeSlots(): TimeSlot[] {
  const slots: TimeSlot[] = [];
  for (let hour = DAY_START_HOUR; hour < DAY_END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += SLOT_INTERVAL) {
      slots.push({
        hour,
        minute,
        label: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
      });
    }
  }
  return slots;
}

/**
 * Get the total number of minutes in the calendar day range.
 */
export function getTotalDayMinutes(): number {
  return (DAY_END_HOUR - DAY_START_HOUR) * 60;
}

/**
 * Calculate the vertical position (as a percentage) of a time within the day.
 */
export function timeToPercent(dateStr: string): number {
  const d = new Date(dateStr);
  const minutesSinceStart = (d.getHours() - DAY_START_HOUR) * 60 + d.getMinutes();
  const total = getTotalDayMinutes();
  return Math.max(0, Math.min(100, (minutesSinceStart / total) * 100));
}

/**
 * Position an appointment within the day grid.
 */
export function positionAppointment(workItem: WorkItem): PositionedAppointment {
  const topPercent = workItem.startAt ? timeToPercent(workItem.startAt) : 0;
  let heightPercent = 100 / (getTotalDayMinutes() / SLOT_INTERVAL); // Default: 1 slot

  if (workItem.startAt && workItem.endAt) {
    const start = new Date(workItem.startAt).getTime();
    const end = new Date(workItem.endAt).getTime();
    const durationMinutes = (end - start) / 60_000;
    heightPercent = (durationMinutes / getTotalDayMinutes()) * 100;
  }

  return {
    workItem,
    topPercent: Math.max(0, topPercent),
    heightPercent: Math.max(heightPercent, 2), // Minimum 2% so it's visible
  };
}

/**
 * Get the Monday of the week containing the given date.
 */
export function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday = 1
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Generate an array of CalendarDayData for the week containing the given date.
 */
export function getWeekDays(date: Date): CalendarDayData[] {
  const monday = getWeekStart(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return {
      date: d,
      dateStr: formatDateStr(d),
      isToday: d.getTime() === today.getTime(),
      isWeekend: false,
    };
  });
}

/**
 * Return a new Date offset from `date` by `days` (may be negative).
 */
export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Format a Date to YYYY-MM-DD string.
 */
export function formatDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Format a Date for display (e.g. "Mon 24 Mar").
 */
export function formatDayHeader(date: Date): string {
  return date.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/**
 * Format a date range for the toolbar (e.g. "24 Mar – 28 Mar 2026").
 */
export function formatWeekRange(weekStart: Date): string {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 4);

  const startStr = weekStart.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  const endStr = weekEnd.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

/**
 * Check if an appointment falls on a given date.
 * Compares using local date to handle UTC-stored ISO strings correctly.
 */
export function isAppointmentOnDate(workItem: WorkItem, dateStr: string): boolean {
  if (!workItem.startAt) return false;
  return formatDateStr(new Date(workItem.startAt)) === dateStr;
}

/**
 * Check if a leave entry overlaps with a given date.
 * Compares using local dates to handle UTC-stored ISO strings correctly.
 */
export function isLeaveOnDate(startAt: string, endAt: string, dateStr: string): boolean {
  const leaveStartDate = formatDateStr(new Date(startAt));
  const leaveEndDate = formatDateStr(new Date(endAt));
  return dateStr >= leaveStartDate && dateStr <= leaveEndDate;
}

/**
 * Get the time string (HH:MM) from a slot click for appointment creation.
 */
export function slotToTimeString(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Build an ISO datetime string from a date string and time slot.
 */
export function buildDateTimeString(dateStr: string, hour: number, minute: number): string {
  return `${dateStr}T${slotToTimeString(hour, minute)}:00`;
}
